import { useDispatch, useSelector } from 'react-redux';

import { ArrowUpOutlined, ContainerOutlined, MailOutlined, StarOutlined } from '@ant-design/icons';
import { App, Button, Col, Input, Row, Space } from 'antd';

import { siteUrls } from '../../../../shared/constants/siteUrls';
import * as actions from '../../store/actions';
import ChatMessagesHeaderActions from './chat-messages-header-actions';

import { isEmpty, isNumber } from 'lodash';
import { AppDispatch, RootState } from '../../../../store';

import './styles.less';
import { ChatsCollection, SmsChat } from '../../models';
import { ArgsProps } from 'antd/es/notification';
import { isGuid } from '../../../../shared/utils';

type ChatMessagesHeaderProps = {
  messageSearchValue: string | undefined;
  setMessageSearchValue: (value: string) => void;
  setPageNumber: (page: number) => void;
  updateMessageControl: () => void;
};

const MESSAGE_PAGE_COUNT = 10;

const ChatMessagesHeader = ({ messageSearchValue, setMessageSearchValue, setPageNumber, updateMessageControl }: ChatMessagesHeaderProps) => {
  const dispatch = useDispatch<AppDispatch>();

  const { notification } = App.useApp();

  const { messageControlParameters, selectedChatItem, localMessages } = useSelector((state: RootState) => state.smses);
  const { localChats } = useSelector((state: RootState) => state.smsChat);

  const fetchMessages = ({ pageNumber, messageSearchValue }: { pageNumber: number; messageSearchValue: string | undefined }) => {
    if (!selectedChatItem?.smsChatId) {
      return;
    }

    dispatch(
      actions.getMessages({
        pageNumber,
        pageSize: MESSAGE_PAGE_COUNT,
        messageSearchValue,
        smsChatId: selectedChatItem.smsChatId,
      }),
    );
  };

  const onMessageSearch = (value: string | undefined) => {
    setPageNumber(1);
    dispatch(actions.setLocalMessages(null));

    fetchMessages({ pageNumber: 1, messageSearchValue: value });
  };

  const onMarkAsRead = (smsChatId: string) => {
    const newChatList = localChats?.chatList?.slice() ?? [];

    const chatListIndex = newChatList.findIndex((x) => x.smsChatId === smsChatId);
    const chatItem = { ...newChatList[chatListIndex] } as SmsChat;

    if (chatItem.unreadCount < 1) {
      notification.info({
        message: 'Info',
        top: 55,
        duration: 10,
        description: `Chat is already marked as read`,
      } as ArgsProps);

      return;
    }

    dispatch(
      actions.markAsRead({
        smsChatId,
      }),
    ).then(() => {
      chatItem.unreadCount = 0;

      newChatList[chatListIndex] = chatItem;

      const newChatItems = { ...localChats } as ChatsCollection;
      newChatItems.chatList = newChatList;

      dispatch(actions.setLocalChats(newChatItems));
      dispatch(actions.setSelectedChatItem(undefined));
      updateMessageControl();
    });
  };

  const onMarkAsUnread = (smsChatId: string, unreadCount: number) => {
    if (localMessages && !(isNumber(unreadCount) && unreadCount > 0)) {
      dispatch(
        actions.markAsUnread({
          smsChatId,
          smsId: localMessages.messageList[localMessages.messageList.length - 1].id,
        }),
      ).then(() => {
        let newChatList = localChats?.chatList?.slice() ?? [];

        let chatListIndex = newChatList.findIndex((x) => x.smsChatId === smsChatId);
        let chatItem = { ...newChatList[chatListIndex] } as SmsChat;

        chatItem.unreadCount++;

        newChatList[chatListIndex] = chatItem;

        let newChatItems = { ...localChats } as ChatsCollection;
        newChatItems.chatList = newChatList;

        dispatch(actions.setLocalChats(newChatItems));
        dispatch(actions.setSelectedChatItem(undefined));
        updateMessageControl();
      });
    }
  };

  const markAsStarred = async ({ smsChatId, starred }: { smsChatId: string; starred: boolean }) => {
    await dispatch(
      actions.markAsStarred({
        smsChatId,

        starred: !starred,
      }),
    );

    notification.success({
      message: 'Success',
      top: 55,
      duration: 10,
      description: `Chat was ${!starred ? 'starred' : 'unstarred'}`,
    } as ArgsProps);

    let newChatList = localChats?.chatList?.slice() ?? [];
    let chatListIndex = newChatList.findIndex((x) => x.smsChatId === smsChatId);
    let chatItem = { ...newChatList[chatListIndex] } as SmsChat;

    chatItem.starred = !starred;
    newChatList[chatListIndex] = chatItem;
    let newChatItems = { ...localChats } as ChatsCollection;
    newChatItems.chatList = newChatList;

    dispatch(actions.setLocalChats(newChatItems));
    dispatch(actions.setSelectedChatItem(chatItem));
    updateMessageControl();
  };

  const onMessageListScrollClick = () => {
    const element = document.getElementById('messagesScrollableDiv');
    if (element) {
      element.scrollTo({ top: -element.scrollHeight, behavior: 'smooth' });
    }
  };

  return (
    <Row className="chat-messages-header">
      <Col span={10} className="chat-messages-header-col">
        {messageControlParameters &&
        selectedChatItem &&
        selectedChatItem.truckingCompanyId &&
        isGuid(selectedChatItem.truckingCompanyId) &&
        selectedChatItem.truckingCompanyType !== undefined ? (
          <Button
            type="link"
            href={siteUrls.getTruckingCompanyDetailsUrl(
              messageControlParameters.isMomentumEnabled,
              selectedChatItem.truckingCompanyId,
              selectedChatItem.truckingCompanyType,
            )}
          >
            <Space>
              <div className="chat-messages-header-col-title">{selectedChatItem?.chatName}</div>

              {selectedChatItem?.phoneNumber}
            </Space>
          </Button>
        ) : (
          <Space>
            <div className="chat-messages-header-col-title">{selectedChatItem?.chatName}</div>

            {selectedChatItem?.phoneNumber}
          </Space>
        )}
      </Col>

      <Col span={4}>
        {selectedChatItem &&
        localMessages &&
        localMessages.messageList &&
        selectedChatItem.unreadCount > 0 &&
        selectedChatItem.unreadCount - localMessages.messageList.filter((x) => x.isRead === false).length > 0 ? (
          <div style={{ position: 'relative', top: '60px', zIndex: 999 }}>
            <Button onClick={onMessageListScrollClick}>
              Unread messages <ArrowUpOutlined />
            </Button>
          </div>
        ) : undefined}
      </Col>

      <Col span={10} className="chat-messages-header-col-right">
        <Space>
          <Button
            title="Mark as starred"
            onClick={
              selectedChatItem
                ? () =>
                    markAsStarred({
                      smsChatId: selectedChatItem?.smsChatId,
                      starred: selectedChatItem?.starred,
                    })
                : undefined
            }
            disabled={isEmpty(selectedChatItem?.smsChatId)}
          >
            <StarOutlined />
          </Button>

          <Button
            title="Mark as unread"
            onClick={() => onMarkAsUnread(selectedChatItem?.smsChatId ?? '', selectedChatItem?.unreadCount ?? 0)}
            disabled={isEmpty(selectedChatItem?.smsChatId)}
          >
            <MailOutlined />
          </Button>

          <Button
            title="Mark as read"
            onClick={() => onMarkAsRead(selectedChatItem?.smsChatId ?? '')}
            disabled={isEmpty(selectedChatItem?.smsChatId)}
          >
            <ContainerOutlined />
          </Button>

          <Input.Search
            allowClear
            onSearch={onMessageSearch}
            disabled={isEmpty(selectedChatItem?.smsChatId)}
            value={messageSearchValue}
            onChange={(e) => setMessageSearchValue(e.target.value)}
            placeholder="Search messages"
            className="message-search"
          />

          <ChatMessagesHeaderActions disabled={isEmpty(selectedChatItem?.smsChatId)} updateMessageControl={updateMessageControl} />
        </Space>
      </Col>
    </Row>
  );
};

export default ChatMessagesHeader;
